/**
 * Admin Billing & Payment Settings
 *
 * Tabs:
 *  1. Invoices      — existing invoice list (unchanged behaviour)
 *  2. Subscriptions — manage subscription tier per company with inline selector
 *
 * Edge cases handled:
 *  - Loading / error states for both tabs
 *  - Optimistic subscription update with rollback on failure
 *  - Confirmation before changing a subscription (destructive action)
 *  - Empty states for both tables
 *  - Search/filter on both tabs
 */

import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Loader } from "react-feather";
import { connect } from "react-redux";
import { Table } from "reactstrap";
import Select, { ValueType } from "react-select";
import { IInvoice } from "../../interfaces/invoice";
import { ICompany } from "../../interfaces/company";
import { IApplicationState } from "../../redux/reducers";
import paymentService from "../../services/paymentService";
import adminService from "../../services/adminService";
import companyService from "../../services/companyService";
import notification from "../../utils/notification";
import { toCamelCase } from "../../utils/string";
import "../../assets/scss/pages/admin/_adminBilling.scss";

// ─── Constants ────────────────────────────────────────────────────────────────

const SUBSCRIPTION_OPTIONS = [
  { label: "Legacy",     value: "legacy"     },
  { label: "Supplier",   value: "supplier"   },
  { label: "Fresh",      value: "fresh"      },
  { label: "Growth",     value: "growth"     },
  { label: "Enterprise", value: "enterprise" },
  { label: "Group",      value: "group"      },
  { label: "Guava",      value: "guava"      },
  { label: "None",       value: ""           },
];

const TIER_COLORS: Record<string, string> = {
  legacy:     "#8b5cf6",
  supplier:   "#3b82f6",
  fresh:      "#10b981",
  growth:     "#f59e0b",
  enterprise: "#ef4444",
  group:      "#ec4899",
  guava:      "#06b6d4",
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface IProps {
  adminInvoices: IInvoice[];
  companies: ICompany[];
  getAllInvoices: () => Promise<any>;
  getCompanies: () => Promise<any>;
  updateCompanySubscription: (subscriptionType: string, companyId: string) => Promise<any>;
}

type TabId = "invoices" | "subscriptions";

// ─── Component ────────────────────────────────────────────────────────────────

const AdminBilling: React.FC<IProps> = ({
  adminInvoices,
  companies,
  getAllInvoices,
  getCompanies,
  updateCompanySubscription,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>("invoices");
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [invoiceSearch, setInvoiceSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");

  // Track which company IDs are currently being updated
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());

  // Local optimistic copy of companies
  const [localCompanies, setLocalCompanies] = useState<ICompany[]>([]);

  // Confirmation state: { companyId, newTier }
  const [pendingChange, setPendingChange] = useState<{ company: ICompany; newTier: string } | null>(null);

  useEffect(() => {
    setLoadingInvoices(true);
    getAllInvoices().finally(() => setLoadingInvoices(false));
  }, []);

  useEffect(() => {
    setLoadingCompanies(true);
    getCompanies().finally(() => setLoadingCompanies(false));
  }, []);

  useEffect(() => {
    setLocalCompanies(companies ?? []);
  }, [companies]);

  // ── Subscription change flow ───────────────────────────────────────────────

  const requestSubscriptionChange = (company: ICompany, newTier: string) => {
    // Don't prompt if same tier
    if ((company.subscription_type ?? "") === newTier) return;
    setPendingChange({ company, newTier });
  };

  const confirmSubscriptionChange = async () => {
    if (!pendingChange) return;
    const { company, newTier } = pendingChange;
    setPendingChange(null);

    // Optimistic update
    setLocalCompanies((prev) =>
      prev.map((c) => (c.id === company.id ? { ...c, subscription_type: newTier } : c))
    );
    setUpdatingIds((prev) => new Set(prev).add(company.id));

    try {
      await updateCompanySubscription(newTier, company.id);
    } catch {
      // Rollback
      setLocalCompanies((prev) =>
        prev.map((c) =>
          c.id === company.id ? { ...c, subscription_type: company.subscription_type } : c
        )
      );
      notification.error(`Failed to update subscription for ${company.name}`);
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(company.id);
        return next;
      });
    }
  };

  // ── Filtered data ──────────────────────────────────────────────────────────

  const filteredInvoices = useMemo(() => {
    if (!invoiceSearch.trim()) return adminInvoices ?? [];
    const q = invoiceSearch.toLowerCase();
    return (adminInvoices ?? []).filter(
      (i) =>
        i.company?.name?.toLowerCase().includes(q) ||
        i.meta_data?.service?.toLowerCase().includes(q) ||
        i.invoice_status?.toLowerCase().includes(q)
    );
  }, [adminInvoices, invoiceSearch]);

  const filteredCompanies = useMemo(() => {
    if (!companySearch.trim()) return localCompanies;
    const q = companySearch.toLowerCase();
    return localCompanies.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
    );
  }, [localCompanies, companySearch]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="admin-billing">
      {/* ── Tab bar ── */}
      <div className="admin-billing__tabs">
        <button
          type="button"
          className={`admin-billing__tab ${activeTab === "invoices" ? "admin-billing__tab--active" : ""}`}
          onClick={() => setActiveTab("invoices")}
        >
          Invoices
        </button>
        <button
          type="button"
          className={`admin-billing__tab ${activeTab === "subscriptions" ? "admin-billing__tab--active" : ""}`}
          onClick={() => setActiveTab("subscriptions")}
        >
          Subscription Management
        </button>
      </div>

      {/* ══ INVOICES TAB ══════════════════════════════════════════════════════ */}
      {activeTab === "invoices" && (
        <div className="admin-billing__panel">
          <div className="admin-billing__panel-header">
            <h4>All Invoices</h4>
            <input
              type="text"
              className="admin-billing__search"
              placeholder="Search by company, service, or status…"
              value={invoiceSearch}
              onChange={(e) => setInvoiceSearch(e.target.value)}
              aria-label="Search invoices"
            />
          </div>

          {loadingInvoices ? (
            <div className="admin-billing__loading">
              <Loader className="fa-spin" size={20} />
              <span>Loading invoices…</span>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="admin-billing__empty">
              <i className="fas fa-file-invoice admin-billing__empty-icon" />
              <p>{invoiceSearch ? "No invoices match your search." : "No invoices found."}</p>
            </div>
          ) : (
            <div className="admin-billing__table-wrap">
              <Table className="admin-billing__table clean-table" responsive hover>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Service</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((i: IInvoice) => (
                    <tr key={i.id}>
                      <td>{i.company?.name ?? "—"}</td>
                      <td>{i.meta_data?.service ?? "—"}</td>
                      <td>{moment(i.created_at).format("MMM D, YYYY")}</td>
                      <td>{moment(i.updated_at).format("MMM D, YYYY")}</td>
                      <td>{i.meta_data?.price ?? "—"}</td>
                      <td>
                        <span
                          className={`admin-billing__status-badge admin-billing__status-badge--${i.invoice_status === "open" ? "open" : "paid"}`}
                        >
                          {toCamelCase(i.invoice_status ?? "")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* ══ SUBSCRIPTIONS TAB ════════════════════════════════════════════════ */}
      {activeTab === "subscriptions" && (
        <div className="admin-billing__panel">
          <div className="admin-billing__panel-header">
            <div>
              <h4>Subscription Management</h4>
              <p className="admin-billing__panel-desc">
                Change a company's subscription tier. A confirmation prompt appears before any change is applied.
              </p>
            </div>
            <input
              type="text"
              className="admin-billing__search"
              placeholder="Search by company name or email…"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              aria-label="Search companies"
            />
          </div>

          {loadingCompanies ? (
            <div className="admin-billing__loading">
              <Loader className="fa-spin" size={20} />
              <span>Loading companies…</span>
            </div>
          ) : filteredCompanies.length === 0 ? (
            <div className="admin-billing__empty">
              <i className="fas fa-building admin-billing__empty-icon" />
              <p>{companySearch ? "No companies match your search." : "No companies found."}</p>
            </div>
          ) : (
            <div className="admin-billing__table-wrap">
              <Table className="admin-billing__table clean-table" responsive hover>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Email</th>
                    <th>Current Tier</th>
                    <th>Period End</th>
                    <th>Trial End</th>
                    <th>Change Tier</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.map((company) => {
                    const isUpdating = updatingIds.has(company.id);
                    const currentTier = company.subscription_type ?? "";

                    return (
                      <tr key={company.id}>
                        <td className="admin-billing__company-name">{company.name}</td>
                        <td>{company.email}</td>
                        <td>
                          {currentTier ? (
                            <span
                              className="admin-billing__tier-badge"
                              style={{ backgroundColor: TIER_COLORS[currentTier] ?? "#6b7280" }}
                            >
                              {toCamelCase(currentTier)}
                            </span>
                          ) : (
                            <span className="admin-billing__tier-badge admin-billing__tier-badge--none">
                              None
                            </span>
                          )}
                        </td>
                        <td>
                          {company.subscription_period_end
                            ? moment(company.subscription_period_end).format("MMM D, YYYY")
                            : "—"}
                        </td>
                        <td>
                          {company.trial_end_date
                            ? moment(company.trial_end_date).format("MMM D, YYYY")
                            : "—"}
                        </td>
                        <td className="admin-billing__select-cell">
                          {isUpdating ? (
                            <div className="admin-billing__updating">
                              <Loader className="fa-spin" size={14} />
                              <span>Updating…</span>
                            </div>
                          ) : (
                            <Select
                              className="admin-billing__tier-select"
                              classNamePrefix="tier-select"
                              value={
                                SUBSCRIPTION_OPTIONS.find((o) => o.value === currentTier) ?? null
                              }
                              onChange={(option: ValueType<{ label: string; value: string }>) => {
                                const selected = option as { label: string; value: string } | null;
                                if (selected) {
                                  requestSubscriptionChange(company, selected.value);
                                }
                              }}
                              options={SUBSCRIPTION_OPTIONS}
                              isDisabled={isUpdating}
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                control: (base) => ({
                                  ...base,
                                  minWidth: 160,
                                  fontSize: 13,
                                  borderColor: "#e5e7eb",
                                }),
                              }}
                              aria-label={`Change subscription for ${company.name}`}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* ── Confirmation modal ── */}
      {pendingChange && (
        <div
          className="admin-billing__confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-title"
        >
          <div className="admin-billing__confirm-modal">
            <h5 id="confirm-title" className="admin-billing__confirm-title">
              Confirm Subscription Change
            </h5>
            <p className="admin-billing__confirm-body">
              Change <strong>{pendingChange.company.name}</strong> from{" "}
              <strong>{toCamelCase(pendingChange.company.subscription_type ?? "None")}</strong> to{" "}
              <strong>{toCamelCase(pendingChange.newTier || "None")}</strong>?
            </p>
            <p className="admin-billing__confirm-warning">
              This will immediately affect the company's feature access.
            </p>
            <div className="admin-billing__confirm-actions">
              <button
                type="button"
                className="admin-billing__confirm-btn admin-billing__confirm-btn--cancel"
                onClick={() => setPendingChange(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-billing__confirm-btn admin-billing__confirm-btn--confirm"
                onClick={confirmSubscriptionChange}
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Redux ────────────────────────────────────────────────────────────────────

const mapStateToProps = (state: IApplicationState) => ({
  adminInvoices: state.billing.admin_invoices ?? [],
  companies: state.company.companies ?? [],
});

const mapDispatchToProps = {
  getAllInvoices: () => paymentService.getAllInvoices(),
  getCompanies: () => companyService.getCompanies(),
  updateCompanySubscription: (subscriptionType: string, companyId: string) =>
    adminService.updateCompanySubscription(subscriptionType, companyId),
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminBilling);
